import type { TranslationKey } from "@/localization/hooks/use-translation";

/**
 * What this application will send, checked before it sends it.
 *
 * These rules are the API's — it re-checks every one of them and its answer is
 * the authoritative one. Checking here is a courtesy that turns a round trip
 * and a generic `invalid_request` into a sentence under the field that names
 * what is wrong, which is the difference between a form a user can finish and
 * one that refuses without saying why.
 *
 * Kept pure and away from the components so the rules are asserted directly,
 * and so the four forms that collect a credential cannot drift into four ideas
 * of what a valid one is.
 */

export const USERNAME_MINIMUM_LENGTH = 3;
export const USERNAME_MAXIMUM_LENGTH = 32;
export const PASSWORD_MINIMUM_LENGTH = 10;
export const PASSWORD_MAXIMUM_LENGTH = 200;

/**
 * ASCII letters and digits, hyphen and underscore, starting with a letter or a
 * digit — the same pattern the API enforces, and for its reasons rather than as
 * a matter of taste: the restriction is what makes case-insensitive uniqueness
 * answerable at all.
 */
const USERNAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;

export const validateUsername = (username: string): TranslationKey | undefined => {
  if (username.length === 0) {
    return "account.error.usernameRequired";
  }
  if (username.length < USERNAME_MINIMUM_LENGTH) {
    return "account.error.usernameTooShort";
  }
  if (username.length > USERNAME_MAXIMUM_LENGTH) {
    return "account.error.usernameTooLong";
  }
  if (!USERNAME_PATTERN.test(username)) {
    return "account.error.usernameCharacters";
  }
  return undefined;
};

/**
 * Length is the whole rule, matching the API. Composition requirements ("one
 * capital, one digit") push people toward `Password1!` and rule out the long
 * passphrase that is actually strong.
 */
export const validateNewPassword = (password: string): TranslationKey | undefined => {
  if (password.length === 0) {
    return "account.error.passwordRequired";
  }
  if (password.length < PASSWORD_MINIMUM_LENGTH) {
    return "account.error.passwordTooShort";
  }
  if (password.length > PASSWORD_MAXIMUM_LENGTH) {
    return "account.error.passwordTooLong";
  }
  return undefined;
};

/**
 * The password being typed now, not a new one, so it is checked only for
 * presence: any other rule here would be this application asserting what the
 * account's existing password must look like, which it cannot know — it may
 * predate a rule, and telling somebody their real password is invalid is worse
 * than letting the API answer.
 */
export const validateCurrentPassword = (password: string): TranslationKey | undefined =>
  password.length === 0 ? "account.error.currentPasswordRequired" : undefined;

export const validatePasswordConfirmation = ({
  confirmation,
  password,
}: {
  confirmation: string;
  password: string;
}): TranslationKey | undefined =>
  password === confirmation ? undefined : "account.error.passwordMismatch";

/**
 * A display name is optional and is the one field here the user writes freely,
 * so the only rule is the length the API will store.
 */
export const DISPLAY_NAME_MAXIMUM_LENGTH = 80;

export const validateDisplayName = (displayName: string): TranslationKey | undefined =>
  displayName.length > DISPLAY_NAME_MAXIMUM_LENGTH ? "account.error.displayNameTooLong" : undefined;

/**
 * Only enough to catch a field left empty or filled with something else: the
 * API and then Polar decide what an address is, and a stricter pattern here
 * would refuse real ones.
 */
export const validatePurchaseEmail = (email: string): TranslationKey | undefined => {
  const trimmed = email.trim();
  if (trimmed.length === 0) {
    return "account.recovery.error.emailRequired";
  }
  return /^[^\s@]+@[^\s@]+$/.test(trimmed) ? undefined : "account.recovery.error.emailInvalid";
};

export const validateRecoveryCode = (code: string): TranslationKey | undefined =>
  code.trim().length === 0 ? "account.recovery.error.codeRequired" : undefined;
